import { createServerFn } from "@tanstack/react-start";
import { type SessionConfig, useSession } from "@tanstack/react-start/server";
import { Environment, graphql, Network } from "relay-runtime";
import { addYears, isAfter, isBefore } from "date-fns";
import { commitMutation } from "react-relay";
import { noAuthFetcher } from "@/relay/network";
import type {
	getSession_refreshTokenMutation,
	getSession_refreshTokenMutation$data,
} from "./__generated__/getSession_refreshTokenMutation.graphql";

export const sessionOptions: SessionConfig = {
	password: import.meta.env.VITE_SECRET_KEY as unknown as string,
	cookie: {
		httpOnly: true,
		secure: true,
		sameSite: "lax",
	},
};

export type SessionData = {
	accessToken?: string;
	refreshToken?: string;
	user?: {
		id: string;
		email: string;
		username?: string | null;
		displayName?: string | null;
		firstName?: string | null;
		lastName?: string | null;
		role?: string | null;
		rawID: string;
		avatarFallback?: string | null;
		photo?: {
			signedUrl?: string | null;
		} | null;
	};
	expiresAt?: string;
	isLoggedIn: boolean;
};

const refreshTokenQuery = graphql`
  mutation getSession_refreshTokenMutation($token: String!) {
      refreshToken(token: $token) {
        accessToken
        refreshToken
        expiresIn
        user {
            id
            email
            username
            displayName
            firstName
            lastName
            role
            avatarFallback
            photo {
              signedUrl
            }
            rawID
        }
      }
  }
`;

const refreshToken = ({
	refreshToken,
}: {
	refreshToken: string;
}): Promise<getSession_refreshTokenMutation$data> => {
	return new Promise((resolve, reject) => {
		commitMutation<getSession_refreshTokenMutation>(
			new Environment({
				network: Network.create(noAuthFetcher()),
			}),
			{
				mutation: refreshTokenQuery,
				variables: {
					token: refreshToken,
				},
				onCompleted: (response) => {
					resolve(response);
				},
				onError: reject,
			},
		);
	});
};

export const getSession = createServerFn().handler(async () => {
	const session = await useSession<SessionData>(sessionOptions);

	if (
		session?.data?.refreshToken &&
		(!session?.data?.expiresAt ||
			isBefore(new Date(session?.data.expiresAt), new Date()) ||
			isAfter(new Date(session?.data.expiresAt), addYears(new Date(), 5)))
	) {
		const response = await refreshToken({
			refreshToken: session?.data.refreshToken,
		});

		if (response.refreshToken?.accessToken) {
			await session?.update({
				...session?.data,
				isLoggedIn: true,
				accessToken: response.refreshToken.accessToken,
				refreshToken: response.refreshToken.refreshToken,
				user: response.refreshToken.user,
				expiresAt: new Date(
					Date.now() + response.refreshToken.expiresIn - 15 * 60 * 1000,
				).toISOString(),
			});

			return { ...session?.data, refreshed: true };
		}
	}

	return session?.data?.accessToken ? session?.data : undefined;
});
